import { type RefObject } from "react";

import { useEventListener, useOnClickOutside } from "usehooks-ts";

import {
  NavKey,
  useKeyboardNav,
} from "src/base-components/EditorTable/hooks/useKeyboardNav";
import {
  useMatrixTableMode,
  useMatrixTableNavActions,
} from "src/matrixTableNode/stores";
import { isFloatingOperatorDropdown } from "src/matrixTableNode/utils";

export const useKeyboardNavigation = (
  tableRef: RefObject<HTMLElement>,
  { rowsLength, columnsLength }: { rowsLength: number; columnsLength: number },
) => {
  const tableMode = useMatrixTableMode();
  const { enableEditingMode, moveColumn, moveRow, reset } =
    useMatrixTableNavActions();

  const maxRow = rowsLength - 1;
  const maxColumn = columnsLength - 1;

  useEventListener(
    "keydown",
    (event) => {
      if (
        !event.shiftKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        event.key.length === 1 &&
        event.key.match(/[a-zA-Z0-9!@#$%^&*()+=._-]/) &&
        tableMode === "select"
      ) {
        enableEditingMode();
      }
    },
    tableRef,
  );

  useKeyboardNav({
    ref: tableRef,
    triggerOnPrevented: true,
    callback: (event: KeyboardEvent, dir: Nullable<NavKey>) => {
      // Edit mode
      if (tableMode === "edit") {
        switch (dir) {
          case NavKey.TAB:
            event.preventDefault();
            moveColumn(event.shiftKey ? -1 : 1, maxColumn);
            reset("editingCell");
            break;

          case NavKey.ESC:
            event.preventDefault();
            reset("editingCell");
            break;

          case NavKey.ENTER: {
            event.preventDefault();
            reset("editingCell");
            moveRow(1, maxRow);
            break;
          }
        }
        return;
      }

      // Select mode
      switch (dir) {
        case NavKey.LEFT:
        case NavKey.RIGHT: {
          event.preventDefault();
          moveColumn(dir === NavKey.LEFT ? -1 : 1, maxColumn);
          break;
        }

        case NavKey.UP:
        case NavKey.DOWN:
          event.preventDefault();
          moveRow(dir === NavKey.UP ? -1 : 1, maxRow);
          break;

        case NavKey.TAB:
          event.preventDefault();
          moveColumn(event.shiftKey ? -1 : 1, maxColumn);
          break;

        case NavKey.ESC:
          event.preventDefault();
          reset();
          break;

        case NavKey.ENTER:
          event.preventDefault();
          enableEditingMode();
          break;
      }
    },
  });

  useOnClickOutside(tableRef, (e) => {
    if (!isFloatingOperatorDropdown(e)) {
      reset();
    }
  });
};
